import { useEffect, useState } from "react";
import API from "../api";
import { auth } from "../firebase";
import {
  LayoutDashboard,
  Building2,
  CheckCircle,
  ArrowLeft,
  X,
  Search
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const { orgSlug } = useParams();
  const [organizations, setOrganizations] = useState([]);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("all");

  // ✅ NEW (loading state)
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = await auth.currentUser?.getIdToken(true);

      const res = await API.get("super-admin/stats/", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(res.data);
    } catch (err) {
      setError("You do not have permission.");
    }
  };

  // 🚀 FIXED (Instant modal + loader)
  const fetchOrganizations = async (type = "all") => {
    try {
      setShowOrgModal(true);   // ✅ open instantly
      setLoadingOrgs(true);    // ✅ show loader
      setOrganizations([]);    // ✅ clear old data
      setFilterType(type);

      const token = await auth.currentUser?.getIdToken(true);

      const res = await API.get("super-admin/organizations/", {
        headers: { Authorization: `Bearer ${token}` }
      });

      let data = res.data || [];

      if (type === "active") {
        data = data.filter((org) => org.status === "active");
      }

      setOrganizations(data);
    } catch (err) {
      alert("Failed to fetch organizations");
    } finally {
      setLoadingOrgs(false); // ✅ stop loader
    }
  };

  // 🔙 BACK BUTTON
  const handleBack = () => {
    window.location.href = `/${orgSlug}/admin-dashboard`;
  };

  const filteredOrgs = organizations.filter((org) =>
    org.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (error) {
    return <div className="p-10 text-center text-red-500">{error}</div>;
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 p-6 md:p-10">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-12">
        <div className="flex items-center gap-4">

          <button
            onClick={handleBack}
            className="p-2 bg-white rounded-full shadow hover:bg-slate-100"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-3 text-slate-800">
              <LayoutDashboard className="text-indigo-600" size={34} />
              Super Admin <span className="text-indigo-600">Panel</span>
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Manage and monitor your platform
            </p>
          </div>

        </div>
      </div>

      {/* STATS */}
      <div className="grid md:grid-cols-2 gap-8">

        <motion.div
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => fetchOrganizations("all")}
          className="cursor-pointer bg-white p-8 rounded-3xl shadow-md border hover:shadow-xl transition-all"
        >
          <div className="flex justify-between items-center mb-6">
            <Building2 size={40} className="text-indigo-600" />
            <span className="text-xs text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full">
              View All
            </span>
          </div>

          <p className="text-sm text-slate-500 mb-2">
            Total Organizations
          </p>

          <h2 className="text-5xl font-bold text-slate-800">
            {stats.totalOrganizations}
          </h2>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => fetchOrganizations("active")}
          className="cursor-pointer bg-white p-8 rounded-3xl shadow-md border hover:shadow-xl transition-all"
        >
          <div className="flex justify-between items-center mb-6">
            <CheckCircle size={40} className="text-green-500" />
            <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
              Active
            </span>
          </div>

          <p className="text-sm text-slate-500 mb-2">
            Active Organizations
          </p>

          <h2 className="text-5xl font-bold text-slate-800">
            {stats.activeOrganizations}
          </h2>
        </motion.div>

      </div>

      {/* MODAL */}
      <AnimatePresence>
        {showOrgModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >

            <motion.div
              className="bg-white w-full max-w-3xl rounded-3xl p-6 shadow-2xl"
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.85 }}
            >

              {/* HEADER */}
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-semibold">
                  {filterType === "active"
                    ? "Active Organizations"
                    : "All Organizations"}
                </h2>

                <button
                  onClick={() => setShowOrgModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {/* SEARCH */}
              <div className="flex items-center gap-2 border px-3 py-2 rounded-xl mb-5 bg-slate-50">
                <Search size={16} />
                <input
                  placeholder="Search organization..."
                  className="w-full bg-transparent outline-none text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* LIST */}
              <div className="max-h-[420px] overflow-y-auto space-y-3">

                {loadingOrgs ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
                  </div>
                ) : filteredOrgs.length === 0 ? (
                  <p className="text-center text-slate-400 py-10">
                    No organizations found
                  </p>
                ) : (
                  filteredOrgs.map((org, index) => (
                    <div
                      key={index}
                      className="p-4 border rounded-2xl bg-slate-50 hover:bg-white flex justify-between items-center"
                    >
                      <div>
                        <h3 className="font-semibold text-slate-800">
                          {org.name}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {org.email || "No email"}
                        </p>
                      </div>

                      <span className="text-xs bg-indigo-100 text-indigo-600 px-3 py-1 rounded-full">
                        {org.status || "Active"}
                      </span>
                    </div>
                  ))
                )}

              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}